import { TournamentGame, TournamentPool } from '../types';

export interface OutcomeScenario {
    label: string;
    description: string;
    points: number;
    projectedSeedRange: string;
    advancementChanceTop8: number; // 0 to 100
    advancementChanceTop4: number; // 0 to 100
    requirements: string[];
    statusType: 'guaranteed' | 'likely' | 'bubble' | 'eliminated';
}

export interface RootingMatchup {
    gameId: string;
    dateTime: string;
    teamA: string;
    teamB: string;
    preferredWinner: string;
    reason: string;
    impact: 'High' | 'Medium' | 'Low';
}

export interface PostRoundRobinTimeSpan {
    stage: string;
    day: string;
    timeSpan: string;
    spanDuration: string;
    locations: string[];
    description: string;
}

export interface PostRoundRobinScenario {
    finishLabel: string;
    stageType: 'playoff' | 'consolation';
    stageBadge: string;
    chancePercent: number;
    statusType: 'guaranteed' | 'likely' | 'bubble' | 'eliminated';
    game4: {
        dateTime: string;
        time: string;
        location: string;
        opponent: string;
        roundName: string;
    };
    sundayPath?: {
        quarterfinal?: {
            dateTime: string;
            time: string;
            location: string;
            opponent: string;
        };
        semifinal?: {
            dateTime: string;
            time: string;
            location: string;
            opponent: string;
        };
        championship?: {
            dateTime: string;
            time: string;
            location: string;
            opponent: string;
        };
    };
}

export interface PostRoundRobinOutlook {
    tournamentName: string;
    overviewSummary: string;
    saturdaySpan: string;
    timeSpans: PostRoundRobinTimeSpan[];
    scenarios: PostRoundRobinScenario[];
}

export interface AdvancementOutlook {
    jetsTeamName: string;
    gamesPlayed: number;
    gamesRemaining: number;
    currentPoints: number;
    currentRecord: { w: number; otw: number; otl: number; l: number };
    currentRank: number;
    totalTeams: number;
    poolName?: string;
    poolRank?: number;
    poolTotalTeams?: number;
    currentQuotient: number;
    goalsFor: number;
    goalsAgainst: number;
    scenarios: OutcomeScenario[];
    rootingGuide: RootingMatchup[];
    keyTakeaway: string;
    isPreliminariesComplete: boolean;
    finalSeedingRank?: number;
    postRoundRobinOutlook?: PostRoundRobinOutlook;
}

/**
 * Normalizes team names for matching (handles variations in naming).
 */
export function isJetsTeam(teamName: string): boolean {
    if (!teamName) return false;
    const lower = teamName.toLowerCase();
    return lower.includes('jets') || lower.includes('chicago jets');
}

/**
 * Calculates current standing rank and total team count for Chicago Jets in tournament pools.
 */
export function calculateTournamentRank(
    pools: TournamentPool[]
): { currentRank: number; totalTeams: number } | null {
    if (!pools || pools.length === 0) return null;

    const standings = pools.flatMap(p => p.standings);
    if (!standings || standings.length === 0) return null;

    const jetsStanding = standings.find(s => isJetsTeam(s.teamName));
    if (!jetsStanding) return null;

    const totalTeams = standings.length;

    let currentRank = 1;
    const sortedStandings = [...standings].sort((a, b) => {
        const ptsA = parseInt(a.pts, 10) || 0;
        const ptsB = parseInt(b.pts, 10) || 0;
        if (ptsB !== ptsA) return ptsB - ptsA;
        const quotA = parseFloat(a.quot) || 0;
        const quotB = parseFloat(b.quot) || 0;
        return quotB - quotA;
    });

    const jetsIdx = sortedStandings.findIndex(s => isJetsTeam(s.teamName));
    if (jetsIdx !== -1) {
        currentRank = jetsIdx + 1;
    }

    return { currentRank, totalTeams };
}

/**
 * Calculates Advancement Outlook and Scenario Probabilities for Chicago Jets in tournament pools.
 */
export function calculateAdvancementOutlook(
    games: TournamentGame[],
    pools: TournamentPool[],
    options?: { tournamentKey?: string }
): AdvancementOutlook | null {
    const standings = pools.flatMap(p => p.standings);
    if (!standings || standings.length === 0) return null;

    const jetsStanding = standings.find(s => isJetsTeam(s.teamName));
    const rankInfo = calculateTournamentRank(pools);
    if (!rankInfo) return null;
    const { currentRank, totalTeams } = rankInfo;

    // Detect Jets pool in multi-pool formats (e.g. Pool E in CCM World Invite)
    const jetsPool = pools.find(p => p.standings.some(s => isJetsTeam(s.teamName)));
    let poolName: string | undefined = undefined;
    let poolRank: number | undefined = undefined;
    let poolTotalTeams: number | undefined = undefined;

    if (jetsPool) {
        poolName = jetsPool.poolName && jetsPool.poolName.length <= 3 ? `Pool ${jetsPool.poolName}` : jetsPool.poolName;
        poolTotalTeams = jetsPool.standings.length;
        const sortedPoolStandings = [...jetsPool.standings].sort((a, b) => {
            const ptsA = parseInt(a.pts, 10) || 0;
            const ptsB = parseInt(b.pts, 10) || 0;
            if (ptsB !== ptsA) return ptsB - ptsA;
            const quotA = parseFloat(a.quot) || 0;
            const quotB = parseFloat(b.quot) || 0;
            return quotB - quotA;
        });
        const poolIdx = sortedPoolStandings.findIndex(s => isJetsTeam(s.teamName));
        if (poolIdx !== -1) {
            poolRank = poolIdx + 1;
        }
    }

    const isMultiPool = pools.length > 1;

    // Find all Jets preliminary games (first 3 scheduled round-robin games)
    const jetsGames = games.filter(g => isJetsTeam(g.homeTeam) || isJetsTeam(g.visitorTeam));
    
    // Preliminary games for Jets are the 3 scheduled round-robin games
    const preliminaryJetsGames = jetsGames.slice(0, 3);
    const completedJetsGames = preliminaryJetsGames.filter(g => {
        const isScheduled = /scheduled/i.test(g.status || '');
        const hasScore = g.homeScore !== '-' && g.visitorScore !== '-' && g.homeScore !== '' && g.visitorScore !== '';
        return !isScheduled && hasScore;
    });

    const gamesPlayed = completedJetsGames.length;
    const gamesRemaining = Math.max(0, 3 - gamesPlayed);

    let w = 0;
    let otw = 0;
    let otl = 0;
    let l = 0;
    let gf = 0;
    let ga = 0;

    completedJetsGames.forEach(g => {
        const isHome = isJetsTeam(g.homeTeam);
        const jScore = parseInt(isHome ? g.homeScore : g.visitorScore, 10) || 0;
        const oScore = parseInt(isHome ? g.visitorScore : g.homeScore, 10) || 0;
        const status = (g.status || '').toLowerCase();

        gf += jScore;
        ga += oScore;

        if (jScore > oScore) {
            if (status.includes('so') || status.includes('ot') || status.includes('shootout')) {
                otw++;
            } else {
                w++;
            }
        } else if (jScore < oScore) {
            if (status.includes('so') || status.includes('ot') || status.includes('shootout')) {
                otl++;
            } else {
                l++;
            }
        }
    });

    const currentPoints = (w * 3) + (otw * 2) + (otl * 1);
    const currentQuotient = (gf + ga > 0) ? (gf / (gf + ga)) : 0;

    const isPreliminariesComplete = gamesPlayed >= 3;

    // Generate outcome scenarios based on games remaining
    const scenarios: OutcomeScenario[] = [];

    const poolLabel = poolName || 'Pool';

    if (gamesRemaining === 3) {
        // Before any games are played (0 games played, 3 remaining)
        if (isMultiPool) {
            scenarios.push({
                label: `Win All 3 ${poolLabel} Games (3-0-0)`,
                description: `Win all 3 ${poolLabel} preliminary games in regulation`,
                points: 9,
                projectedSeedRange: `${poolLabel} #1 Seed (Sweet 16 Playoff)`,
                advancementChanceTop8: 100,
                advancementChanceTop4: 85,
                requirements: [
                    `Clinches #1 in ${poolLabel} and secures top seed in Saturday's Sweet 16 Playoff against Pool D #2 (Sat 2:50 PM @ Orbit).`,
                    'Saturday playoff winner advances to Sunday morning Quarterfinals (7:10 AM @ Elk Grove).'
                ],
                statusType: 'guaranteed'
            });

            scenarios.push({
                label: `Win 2 of 3 ${poolLabel} Games (2-1-0 • 6-7 pts)`,
                description: `2 Wins and 1 Loss / OT Loss in ${poolLabel}`,
                points: 6,
                projectedSeedRange: `${poolLabel} #1 – #2 Seed (Sweet 16 Playoff)`,
                advancementChanceTop8: 90,
                advancementChanceTop4: 50,
                requirements: [
                    `Strong position to finish Top 2 in ${poolLabel} and clinch a Saturday Sweet 16 Playoff spot.`,
                    `1st in ${poolLabel} plays D2nd (Sat 2:50 PM @ Orbit); 2nd in ${poolLabel} plays D1st (Sat 2:10 PM @ Rosemont).`,
                    'Head-to-head and goal quotient (GF / GF+GA) will determine 1st vs 2nd tiebreaker if tied at 6 points.'
                ],
                statusType: 'likely'
            });

            scenarios.push({
                label: `Win 1 of 3 ${poolLabel} Games (1-2-0 • 3-4 pts)`,
                description: `1 Win and 2 Losses in ${poolLabel}`,
                points: 3,
                projectedSeedRange: `${poolLabel} #3 Seed (Consolation)`,
                advancementChanceTop8: 15,
                advancementChanceTop4: 0,
                requirements: [
                    `Typically finishes 3rd in ${poolLabel}, leading to Saturday Consolation vs Pool D #3 (Sat 1:55 PM @ Mount Prospect).`,
                    `Can only reach Top 2 Sweet 16 with a 3-way tie at 3 points in ${poolLabel} and a superior goal quotient.`
                ],
                statusType: 'bubble'
            });

            scenarios.push({
                label: `0 Wins (0-3-0 • 0-2 pts)`,
                description: `3 Losses / Shootout Losses in ${poolLabel}`,
                points: 1,
                projectedSeedRange: `${poolLabel} #4 Seed (Consolation)`,
                advancementChanceTop8: 0,
                advancementChanceTop4: 0,
                requirements: [
                    'Eliminated from Championship Playoff contention.',
                    'Plays in Saturday Consolation game against Pool D #4 (Sat 1:55 PM @ Elk Grove East).'
                ],
                statusType: 'eliminated'
            });
        } else {
            scenarios.push({
                label: 'Win All 3 Games (3-0-0)',
                description: 'Win all preliminary games in regulation',
                points: 9,
                projectedSeedRange: '#1 – #3 Seed',
                advancementChanceTop8: 100,
                advancementChanceTop4: 95,
                requirements: [
                    'Guarantees #1–#4 Seed and a top spot in Saturday Evening Playoff Qualifiers.',
                    'Direct path to Sunday Semifinals without relying on other team results.'
                ],
                statusType: 'guaranteed'
            });

            scenarios.push({
                label: 'Win 2 of 3 Games (2-1-0 • 6-7 pts)',
                description: '2 Wins and 1 Loss / OT Loss',
                points: 6,
                projectedSeedRange: '#4 – #8 Seed',
                advancementChanceTop8: 92,
                advancementChanceTop4: 55,
                requirements: [
                    'Strong position to clinch a Top 8 Playoff Qualifier spot.',
                    'Top 4 seed depends on goal quotient and having fewer than 4 teams finish with 7+ points.',
                    'Goal quotient (GF / GF+GA) and keeping Goals Against low will be critical.'
                ],
                statusType: 'likely'
            });

            scenarios.push({
                label: 'Win 1 of 3 Games (1-2-0 • 3-4 pts)',
                description: '1 Win and 2 Losses',
                points: 3,
                projectedSeedRange: '#9 – #14 Seed',
                advancementChanceTop8: 20,
                advancementChanceTop4: 5,
                requirements: [
                    'Unlikely to make Top 8 Playoff Qualifiers; usually places team in Saturday Consolation matchups.',
                    'Requires multiple upsets across other preliminary games and a high goal quotient.'
                ],
                statusType: 'bubble'
            });

            scenarios.push({
                label: '0 Wins (0-3-0 • 0-2 pts)',
                description: '3 Losses / Shootout Losses',
                points: 1,
                projectedSeedRange: '#15 – #18 Seed',
                advancementChanceTop8: 0,
                advancementChanceTop4: 0,
                requirements: [
                    'Eliminated from Championship Playoff contention.',
                    'Plays in Saturday Evening Consolation game (#15–#18 placement).'
                ],
                statusType: 'eliminated'
            });
        }
    } else if (gamesRemaining === 2) {
        // 1 game played, 2 remaining
        const maxPoints = currentPoints + 6;
        const midPoints = currentPoints + 3;

        if (isMultiPool) {
            if (w === 1 || otw === 1) {
                // Started 1-0-0 (3 pts) or 1-0-0 OTW (2 pts)
                scenarios.push({
                    label: `Win Next 2 Games (${maxPoints} pts total)`,
                    description: `Finish ${poolLabel} 3-0-0 with ${maxPoints} points`,
                    points: maxPoints,
                    projectedSeedRange: `${poolLabel} #1 Seed (Sweet 16 Playoff)`,
                    advancementChanceTop8: 100,
                    advancementChanceTop4: 85,
                    requirements: [
                        `Clinches #1 in ${poolLabel} and secures top seed in Saturday's Sweet 16 Playoff against Pool D #2 (Sat 2:50 PM @ Orbit).`,
                        'Winner advances to Sunday morning Quarterfinals (7:10 AM @ Elk Grove).'
                    ],
                    statusType: 'guaranteed'
                });

                scenarios.push({
                    label: `Split Next 2 Games (${midPoints} pts total)`,
                    description: `Finish ${poolLabel} 2-1-0 with ${midPoints} points`,
                    points: midPoints,
                    projectedSeedRange: `${poolLabel} #1 – #2 Seed (Sweet 16 Playoff)`,
                    advancementChanceTop8: 90,
                    advancementChanceTop4: 50,
                    requirements: [
                        `Very likely clinches a Top 2 finish in ${poolLabel} for Saturday's Sweet 16 Playoff.`,
                        '1st plays D2nd @ Orbit (Sat 2:50 PM); 2nd plays D1st @ Rosemont (Sat 2:10 PM).',
                        'Goal quotient will decide 1st vs 2nd if tied at 6 points.'
                    ],
                    statusType: 'likely'
                });

                scenarios.push({
                    label: `Lose Next 2 Games (${currentPoints} pts total)`,
                    description: `Finish ${poolLabel} 1-2-0 with ${currentPoints} points`,
                    points: currentPoints,
                    projectedSeedRange: `${poolLabel} #3 Seed (Consolation)`,
                    advancementChanceTop8: 15,
                    advancementChanceTop4: 0,
                    requirements: [
                        `Requires a 3-way tie at 3 points in ${poolLabel} to contest a Top 2 playoff spot.`,
                        'Otherwise seeds into Saturday Consolation vs Pool D #3 (Sat 1:55 PM @ Mount Prospect).'
                    ],
                    statusType: 'bubble'
                });
            } else {
                // Started 0-1-0 (0-1 pts)
                scenarios.push({
                    label: `Win Next 2 Games (${maxPoints} pts total)`,
                    description: `Finish ${poolLabel} 2-1-0 with ${maxPoints} points`,
                    points: maxPoints,
                    projectedSeedRange: `${poolLabel} #1 – #2 Seed (Sweet 16 Playoff)`,
                    advancementChanceTop8: 88,
                    advancementChanceTop4: 45,
                    requirements: [
                        `Winning both remaining pool games is crucial to clinch a Top 2 Sweet 16 Playoff spot in ${poolLabel}.`,
                        'Maximize Goal Quotient with strong goal differential to win tiebreakers.'
                    ],
                    statusType: 'likely'
                });

                scenarios.push({
                    label: `Win 1 of Next 2 Games (${midPoints} pts total)`,
                    description: `Finish ${poolLabel} 1-2-0 with ${midPoints} points`,
                    points: midPoints,
                    projectedSeedRange: `${poolLabel} #3 Seed (Consolation)`,
                    advancementChanceTop8: 18,
                    advancementChanceTop4: 0,
                    requirements: [
                        `Requires a 3-way tie and favorable goal quotient to challenge for #2 in ${poolLabel}.`,
                        'Otherwise placed in Saturday Consolation vs Pool D #3 (Sat 1:55 PM @ Mount Prospect).'
                    ],
                    statusType: 'bubble'
                });
            }
        } else {
            if (w === 1 || otw === 1) {
                // Started 1-0-0 (3 pts) or 1-0-0 OTW (2 pts)
                scenarios.push({
                    label: `Win Next 2 Games (${maxPoints} pts total)`,
                    description: `Finish preliminary round 3-0-0 with ${maxPoints} points`,
                    points: maxPoints,
                    projectedSeedRange: '#1 – #3 Seed',
                    advancementChanceTop8: 100,
                    advancementChanceTop4: 95,
                    requirements: [
                        'Guarantees a Top 4 seed in Saturday Playoff Qualifiers.',
                        'Direct path to Sunday Semifinals.'
                    ],
                    statusType: 'guaranteed'
                });

                scenarios.push({
                    label: `Split Next 2 Games (${midPoints} pts total)`,
                    description: `Finish preliminary round 2-1-0 with ${midPoints} points`,
                    points: midPoints,
                    projectedSeedRange: '#4 – #8 Seed',
                    advancementChanceTop8: 90,
                    advancementChanceTop4: 50,
                    requirements: [
                        'Very likely secures a Top 8 Playoff spot.',
                        'Keep Goals Against minimal to maximize goal quotient against other 2-1 teams.'
                    ],
                    statusType: 'likely'
                });

                scenarios.push({
                    label: `Lose Next 2 Games (${currentPoints} pts total)`,
                    description: `Finish preliminary round 1-2-0 with ${currentPoints} points`,
                    points: currentPoints,
                    projectedSeedRange: '#9 – #14 Seed',
                    advancementChanceTop8: 15,
                    advancementChanceTop4: 2,
                    requirements: [
                        'Requires help from other teams dropping points to stay on the Top 8 bubble.'
                    ],
                    statusType: 'bubble'
                });
            } else {
                // Started 0-1-0 (0-1 pts)
                scenarios.push({
                    label: `Win Next 2 Games (${maxPoints} pts total)`,
                    description: `Finish preliminary round 2-1-0 with ${maxPoints} points`,
                    points: maxPoints,
                    projectedSeedRange: '#4 – #8 Seed',
                    advancementChanceTop8: 88,
                    advancementChanceTop4: 45,
                    requirements: [
                        'Winning both remaining games is crucial to enter the Top 8 Playoff bracket.',
                        'Maximize Goal Quotient with strong goal differential.'
                    ],
                    statusType: 'likely'
                });

                scenarios.push({
                    label: `Win 1 of Next 2 Games (${midPoints} pts total)`,
                    description: `Finish preliminary round 1-2-0 with ${midPoints} points`,
                    points: midPoints,
                    projectedSeedRange: '#9 – #14 Seed',
                    advancementChanceTop8: 18,
                    advancementChanceTop4: 0,
                    requirements: [
                        'Requires multiple upsets across division games to reach Top 8 seed.'
                    ],
                    statusType: 'bubble'
                });
            }
        }
    } else if (gamesRemaining === 1) {
        // 2 games played, 1 remaining
        const maxPoints = currentPoints + 3;

        if (isMultiPool) {
            scenarios.push({
                label: `Win Final Game (${maxPoints} pts total)`,
                description: `Finish ${poolLabel} with ${maxPoints} points`,
                points: maxPoints,
                projectedSeedRange: maxPoints >= 6 ? `${poolLabel} #1 – #2 Seed (Sweet 16 Playoff)` : `${poolLabel} #2 – #3 Seed`,
                advancementChanceTop8: maxPoints >= 6 ? 95 : 45,
                advancementChanceTop4: maxPoints >= 6 ? 60 : 5,
                requirements: [
                    maxPoints >= 6
                        ? `Secures a Top 2 finish in ${poolLabel} and a Sweet 16 Playoff spot on Saturday afternoon.`
                        : `Must win and hold higher goal quotient than other tied teams to reach Sweet 16 Playoff.`
                ],
                statusType: maxPoints >= 6 ? 'likely' : 'bubble'
            });

            scenarios.push({
                label: `Loss in Final Game (${currentPoints} pts total)`,
                description: `Finish ${poolLabel} with ${currentPoints} points`,
                points: currentPoints,
                projectedSeedRange: currentPoints >= 6 ? `${poolLabel} #2 Seed (Sweet 16 Playoff)` : `${poolLabel} #3 – #4 Seed (Consolation)`,
                advancementChanceTop8: currentPoints >= 6 ? 70 : 10,
                advancementChanceTop4: currentPoints >= 6 ? 25 : 0,
                requirements: [
                    currentPoints >= 6
                        ? `Top 2 finish in ${poolLabel} depends on Saturday morning pool results and goal quotient.`
                        : `Eliminated from Sweet 16 Playoff; seeded into Saturday Consolation game (#49 or #50).`
                ],
                statusType: currentPoints >= 6 ? 'bubble' : 'eliminated'
            });
        } else {
            scenarios.push({
                label: `Win Final Game (${maxPoints} pts total)`,
                description: `Finish round robin with ${maxPoints} points`,
                points: maxPoints,
                projectedSeedRange: maxPoints >= 7 ? '#1 – #4 Seed' : (maxPoints >= 6 ? '#4 – #8 Seed' : '#8 – #12 Seed'),
                advancementChanceTop8: maxPoints >= 6 ? 95 : 45,
                advancementChanceTop4: maxPoints >= 7 ? 85 : (maxPoints >= 6 ? 40 : 5),
                requirements: [
                    maxPoints >= 6 
                        ? 'Secures Top 8 playoff qualifier spot for Saturday evening.'
                        : 'Must win and have higher goal quotient than other tied teams.'
                ],
                statusType: maxPoints >= 6 ? 'likely' : 'bubble'
            });

            scenarios.push({
                label: `Loss in Final Game (${currentPoints} pts total)`,
                description: `Finish round robin with ${currentPoints} points`,
                points: currentPoints,
                projectedSeedRange: currentPoints >= 6 ? '#6 – #9 Seed' : '#10 – #16 Seed',
                advancementChanceTop8: currentPoints >= 6 ? 70 : 10,
                advancementChanceTop4: currentPoints >= 6 ? 20 : 0,
                requirements: [
                    currentPoints >= 6
                        ? 'Top 8 spot depends on outcome of other Saturday midday games and goal quotient.'
                        : 'Eliminated from Top 8; seeded into Saturday Consolation game.'
                ],
                statusType: currentPoints >= 6 ? 'bubble' : 'eliminated'
            });
        }
    } else {
        // All 3 preliminary games complete
        if (isMultiPool) {
            const isPlayoffs = (poolRank ?? 4) <= 2;
            const seedText = poolRank === 1 
                ? `${poolLabel} #1 Seed (Sweet 16 Playoff)` 
                : (poolRank === 2 
                    ? `${poolLabel} #2 Seed (Sweet 16 Playoff)` 
                    : (poolRank === 3 
                        ? `${poolLabel} #3 Seed (Consolation)` 
                        : `${poolLabel} #4 Seed (Consolation)`));

            scenarios.push({
                label: `Final Pool Record: ${w}-${otw}-${otl}-${l} (${currentPoints} pts)`,
                description: `Finished #${poolRank} in ${poolLabel} Standings`,
                points: currentPoints,
                projectedSeedRange: seedText,
                advancementChanceTop8: isPlayoffs ? 100 : 0,
                advancementChanceTop4: poolRank === 1 ? 75 : (poolRank === 2 ? 50 : 0),
                requirements: [
                    poolRank === 1
                        ? `Clinched #1 in ${poolLabel}! Advances to Saturday afternoon Sweet 16 Playoff vs Pool D #2 (Sat 2:50 PM @ Orbit). Winner plays Sunday Quarterfinals.`
                        : (poolRank === 2
                            ? `Clinched #2 in ${poolLabel}! Advances to Saturday afternoon Sweet 16 Playoff vs Pool D #1 (Sat 2:10 PM @ Rosemont). Winner plays Sunday Quarterfinals.`
                            : (poolRank === 3
                                ? `Finished #3 in ${poolLabel}. Playing Saturday Consolation vs Pool D #3 (Sat 1:55 PM @ Mount Prospect).`
                                : `Finished #4 in ${poolLabel}. Playing Saturday Consolation vs Pool D #4 (Sat 1:55 PM @ Elk Grove).`))
                ],
                statusType: isPlayoffs ? 'guaranteed' : 'eliminated'
            });
        } else {
            scenarios.push({
                label: `Final Round Robin Record: ${w}-${otw}-${otl}-${l} (${currentPoints} pts)`,
                description: `Seeded #${currentRank} in Division Standings`,
                points: currentPoints,
                projectedSeedRange: `#${currentRank} Seed`,
                advancementChanceTop8: currentRank <= 8 ? 100 : 0,
                advancementChanceTop4: currentRank <= 4 ? 100 : 0,
                requirements: [
                    currentRank <= 8
                        ? `Qualified for Saturday Evening Playoff Qualifier as Seed #${currentRank}!`
                        : `Placed in Saturday Evening Consolation matchup as Seed #${currentRank}.`
                ],
                statusType: currentRank <= 8 ? 'guaranteed' : 'eliminated'
            });
        }
    }

    // Rooting Guide: Identify key games among other teams
    const rootingGuide: RootingMatchup[] = isMultiPool ? [
        {
            gameId: 'pool-e-game-2',
            dateTime: 'Friday 12:30 PM CDT',
            teamA: 'Winnetka Warriors 2014 CS',
            teamB: 'Traverse City North Stars',
            preferredWinner: 'Traverse City North Stars (or OT Split)',
            reason: 'Keeps Winnetka from banking 3 regulation points in Pool E ahead of Saturday morning matchup.',
            impact: 'High'
        },
        {
            gameId: 'pool-e-game-3',
            dateTime: 'Friday 06:10 PM CDT',
            teamA: 'California Wave 12AA',
            teamB: 'Winnetka Warriors 2014 CS',
            preferredWinner: 'California Wave 12AA',
            reason: 'A California Wave win or regulation split keeps Pool E standings favorable for the Jets.',
            impact: 'High'
        },
        {
            gameId: 'pool-e-game-6',
            dateTime: 'Saturday 09:10 AM CDT',
            teamA: 'California Wave 12AA',
            teamB: 'Traverse City North Stars',
            preferredWinner: 'Regulation Result (Avoid OT)',
            reason: 'Final Pool E preliminary game after Jets-Winnetka. Determines final pool tiebreakers and goal quotient rankings.',
            impact: 'Medium'
        }
    ] : [
        {
            gameId: 'game-4',
            dateTime: 'Friday 09:10 AM EDT',
            teamA: 'Midland Northstars',
            teamB: 'Vipers - 2014',
            preferredWinner: 'Regulation Win (Avoid OT)',
            reason: 'Jets play Midland Northstars in Game 2. A regulation result prevents bonus 3-point split.',
            impact: 'Medium'
        },
        {
            gameId: 'game-6',
            dateTime: 'Friday 10:35 AM EDT',
            teamA: 'Hartland Hockey Club Black',
            teamB: 'Arapahoe Warriors 12UAA',
            preferredWinner: 'Arapahoe Warriors 12UAA',
            reason: 'Jets play Hartland in Game 3 on Saturday. A Hartland loss keeps their point ceiling lower.',
            impact: 'High'
        },
        {
            gameId: 'game-14',
            dateTime: 'Friday 04:15 PM EDT',
            teamA: 'Arapahoe Warriors 12UAA',
            teamB: 'Traverse City North Stars',
            preferredWinner: 'Arapahoe Warriors 12UAA',
            reason: 'Traverse City is in the Jets preliminary pod. A loss suppresses their standing points.',
            impact: 'High'
        },
        {
            gameId: 'game-16',
            dateTime: 'Friday 05:40 PM EDT',
            teamA: 'Vipers - 2014',
            teamB: 'Hartland Hockey Club Black',
            preferredWinner: 'Vipers - 2014',
            reason: 'Keeps Hartland from accumulating points ahead of Saturday noon matchup.',
            impact: 'Medium'
        }
    ];

    // Formulate key takeaway
    let keyTakeaway = '';
    if (isMultiPool) {
        const poolShort = poolName ? poolName.replace(/^Pool\s*/i, 'Pool ') : 'Pool E';
        if (gamesRemaining === 3) {
            keyTakeaway = `Target: Finish Top 2 in ${poolShort} (6+ pts) to lock in a spot in Saturday's Championship Sweet 16 Playoff bracket.`;
        } else if (gamesRemaining === 2) {
            keyTakeaway = currentPoints >= 3
                ? `Great Start! 1 more win puts the Jets in prime position to clinch a Top 2 finish in ${poolShort} for Saturday's Sweet 16 Playoff.`
                : `Must-Win Territory: Winning both remaining pool games is needed to contend for a Top 2 playoff spot in ${poolShort}.`;
        } else if (gamesRemaining === 1) {
            keyTakeaway = currentPoints >= 6
                ? `Playoff Spot in Sight! A victory in Game 3 locks up ${poolShort} top seeding for Saturday's Sweet 16 Playoff against Pool D.`
                : `Decisive Game 3: A victory is needed to maximize points and goal quotient for a Top 2 playoff spot in ${poolShort}.`;
        } else {
            keyTakeaway = poolRank === 1
                ? `Jets finish #1 in ${poolShort}! Seeded into Saturday afternoon's Sweet 16 Playoff against Pool D #2 (Sat 2:50 PM @ Orbit).`
                : (poolRank === 2
                    ? `Jets finish #2 in ${poolShort}! Seeded into Saturday afternoon's Sweet 16 Playoff against Pool D #1 (Sat 2:10 PM @ Rosemont).`
                    : `Jets finish #${poolRank} in ${poolShort}. Playing in Saturday afternoon Consolation crossover against Pool D #${poolRank}.`);
        }
    } else {
        if (gamesRemaining === 3) {
            keyTakeaway = 'Target: 6+ Points (2 Wins) virtually guarantees a Top 8 Playoff spot, while 3 Wins (9 pts) locks in a Top 4 Seed for Sunday Semifinal contention.';
        } else if (gamesRemaining === 2) {
            keyTakeaway = currentPoints >= 3
                ? 'Great Start! 1 more regulation win (6 pts total) puts the Jets in prime position for a Top 8 Playoff seed.'
                : 'Must-Win Territory: Winning both remaining preliminary games is needed to secure a Top 8 Playoff spot.';
        } else if (gamesRemaining === 1) {
            keyTakeaway = currentPoints >= 6
                ? 'Win and Secure Top 4: A win in Game 3 cements a premier playoff seeding.'
                : 'Decisive Game 3: A victory is needed to maximize goal quotient and qualify for the Top 8.';
        } else {
            keyTakeaway = currentRank <= 8
                ? `Jets finish preliminary round seeded #${currentRank}. Ready for Saturday Evening Playoff action!`
                : `Jets finish seeded #${currentRank}. Playing in Saturday Evening Consolation round.`;
        }
    }

    const postRoundRobinOutlook = generatePostRoundRobinOutlook(
        options?.tournamentKey,
        gamesPlayed,
        currentPoints,
        poolRank,
        currentRank,
        isPreliminariesComplete
    );

    return {
        jetsTeamName: jetsStanding?.teamName || "'14 Chicago Jets",
        gamesPlayed,
        gamesRemaining,
        currentPoints,
        currentRecord: { w, otw, otl, l },
        currentRank,
        totalTeams,
        poolName,
        poolRank,
        poolTotalTeams,
        currentQuotient,
        goalsFor: gf,
        goalsAgainst: ga,
        scenarios,
        rootingGuide,
        keyTakeaway,
        isPreliminariesComplete,
        postRoundRobinOutlook
    };
}

/**
 * Generates structured breakdown of post-round-robin possible game times, rinks, and scenarios for parents.
 */
export function generatePostRoundRobinOutlook(
    tournamentKey: string | undefined,
    gamesPlayed: number,
    currentPoints: number,
    poolRank: number | undefined,
    currentRank: number | undefined,
    isPreliminariesComplete: boolean
): PostRoundRobinOutlook | undefined {
    const getStatus = (p: number): 'guaranteed' | 'likely' | 'bubble' | 'eliminated' => {
        if (p >= 90) return 'guaranteed';
        if (p >= 50) return 'likely';
        if (p > 0) return 'bubble';
        return 'eliminated';
    };

    if (tournamentKey === 'ccm-world-invite-2026') {
        let pE1st = 25;
        let pE2nd = 25;
        let pE3rd = 25;
        let pE4th = 25;

        if (isPreliminariesComplete && poolRank) {
            pE1st = poolRank === 1 ? 100 : 0;
            pE2nd = poolRank === 2 ? 100 : 0;
            pE3rd = poolRank === 3 ? 100 : 0;
            pE4th = poolRank === 4 ? 100 : 0;
        } else if (gamesPlayed === 0) {
            pE1st = 30;
            pE2nd = 30;
            pE3rd = 25;
            pE4th = 15;
        } else if (gamesPlayed === 1) {
            if (currentPoints >= 3) {
                pE1st = 50;
                pE2nd = 35;
                pE3rd = 12;
                pE4th = 3;
            } else if (currentPoints >= 1) {
                pE1st = 25;
                pE2nd = 35;
                pE3rd = 25;
                pE4th = 15;
            } else {
                pE1st = 8;
                pE2nd = 22;
                pE3rd = 45;
                pE4th = 25;
            }
        } else if (gamesPlayed === 2) {
            if (currentPoints >= 6) {
                pE1st = 70;
                pE2nd = 30;
                pE3rd = 0;
                pE4th = 0;
            } else if (currentPoints >= 4) {
                pE1st = 40;
                pE2nd = 45;
                pE3rd = 15;
                pE4th = 0;
            } else if (currentPoints === 3) {
                pE1st = 15;
                pE2nd = 45;
                pE3rd = 30;
                pE4th = 10;
            } else {
                pE1st = 0;
                pE2nd = 5;
                pE3rd = 45;
                pE4th = 50;
            }
        }

        return {
            tournamentName: '2026 CCM World Invite (12U Jetspeed)',
            overviewSummary: 'All teams are guaranteed 4 games. Following 3 pool games, Saturday Game 4 takes place between 1:55 PM and 2:50 PM CDT regardless of pool finish. Teams finishing 1st or 2nd in Pool E advance to the Sweet 16 Playoff bracket on Saturday afternoon, playing for Sunday Quarterfinal spots.',
            saturdaySpan: '01:55 PM – 02:50 PM CDT',
            timeSpans: [
                {
                    stage: 'Saturday Game 4 (Playoff & Consolation)',
                    day: 'Saturday, Nov 7',
                    timeSpan: '01:55 PM – 02:50 PM CDT',
                    spanDuration: '55-minute window',
                    locations: ['Orbit Ice Arena', 'Rosemont Ice Arena (East)', 'Mount Prospect Ice Arena (South)', 'Elk Grove Ice Arena (East)'],
                    description: 'All 4 potential Pool E finish scenarios play during this tight afternoon window.'
                },
                {
                    stage: 'Sunday Quarterfinals (Elite 8)',
                    day: 'Sunday, Nov 8',
                    timeSpan: '07:10 AM – 08:20 AM CDT',
                    spanDuration: '70-minute window',
                    locations: ['Elk Grove Ice Arena (East Rink)'],
                    description: 'Top 2 pool finishers who win Saturday advance to early Sunday morning Quarterfinals.'
                },
                {
                    stage: 'Sunday Semifinals (Frozen 4)',
                    day: 'Sunday, Nov 8',
                    timeSpan: '10:20 AM – 11:30 AM CDT',
                    spanDuration: '70-minute window',
                    locations: ['North Shore Ice Arena'],
                    description: 'Semifinals for Quarterfinal winners.'
                },
                {
                    stage: 'Sunday Championship Final',
                    day: 'Sunday, Nov 8',
                    timeSpan: '02:45 PM CDT',
                    spanDuration: 'Single game',
                    locations: ['North Shore Ice Arena'],
                    description: 'Championship game to crown the 12U Jetspeed tournament champion.'
                }
            ],
            scenarios: [
                {
                    finishLabel: '1st in Pool E (E1st)',
                    stageType: 'playoff',
                    stageBadge: 'Sweet 16 Playoff',
                    chancePercent: pE1st,
                    statusType: getStatus(pE1st),
                    game4: {
                        dateTime: 'Saturday, Nov 7 • 02:50 PM CDT',
                        time: '02:50 PM CDT',
                        location: 'Orbit Ice Arena',
                        opponent: 'vs Pool D 2nd Place (D2nd)',
                        roundName: 'Round of 16 (Game #52)'
                    },
                    sundayPath: {
                        quarterfinal: {
                            dateTime: 'Sunday, Nov 8 • 07:10 AM CDT',
                            time: '07:10 AM CDT',
                            location: 'Elk Grove Ice Arena (East Rink)',
                            opponent: 'vs Winner of Game 55'
                        },
                        semifinal: {
                            dateTime: 'Sunday, Nov 8 • 10:20 AM CDT',
                            time: '10:20 AM CDT',
                            location: 'North Shore Ice Arena',
                            opponent: 'vs Winner of Game 65'
                        },
                        championship: {
                            dateTime: 'Sunday, Nov 8 • 02:45 PM CDT',
                            time: '02:45 PM CDT',
                            location: 'North Shore Ice Arena',
                            opponent: 'vs Winner of Game 70'
                        }
                    }
                },
                {
                    finishLabel: '2nd in Pool E (E2nd)',
                    stageType: 'playoff',
                    stageBadge: 'Sweet 16 Playoff',
                    chancePercent: pE2nd,
                    statusType: getStatus(pE2nd),
                    game4: {
                        dateTime: 'Saturday, Nov 7 • 02:10 PM CDT',
                        time: '02:10 PM CDT',
                        location: 'Rosemont Ice Arena (East Rink)',
                        opponent: 'vs Pool D 1st Place (D1st)',
                        roundName: 'Round of 16 (Game #51)'
                    },
                    sundayPath: {
                        quarterfinal: {
                            dateTime: 'Sunday, Nov 8 • 08:20 AM CDT',
                            time: '08:20 AM CDT',
                            location: 'Elk Grove Ice Arena (East Rink)',
                            opponent: 'vs Winner of Game 62'
                        },
                        semifinal: {
                            dateTime: 'Sunday, Nov 8 • 11:30 AM CDT',
                            time: '11:30 AM CDT',
                            location: 'North Shore Ice Arena',
                            opponent: 'vs Winner of Game 67'
                        },
                        championship: {
                            dateTime: 'Sunday, Nov 8 • 02:45 PM CDT',
                            time: '02:45 PM CDT',
                            location: 'North Shore Ice Arena',
                            opponent: 'vs Winner of Game 69'
                        }
                    }
                },
                {
                    finishLabel: '3rd in Pool E (E3rd)',
                    stageType: 'consolation',
                    stageBadge: 'Consolation Game 4',
                    chancePercent: pE3rd,
                    statusType: getStatus(pE3rd),
                    game4: {
                        dateTime: 'Saturday, Nov 7 • 01:55 PM CDT',
                        time: '01:55 PM CDT',
                        location: 'Mount Prospect Ice Arena (South Rink)',
                        opponent: 'vs Pool D 3rd Place (D3rd)',
                        roundName: 'Consolation Placement (Game #49)'
                    }
                },
                {
                    finishLabel: '4th in Pool E (E4th)',
                    stageType: 'consolation',
                    stageBadge: 'Consolation Game 4',
                    chancePercent: pE4th,
                    statusType: getStatus(pE4th),
                    game4: {
                        dateTime: 'Saturday, Nov 7 • 01:55 PM CDT',
                        time: '01:55 PM CDT',
                        location: 'Elk Grove Ice Arena (East Rink)',
                        opponent: 'vs Pool D 4th Place (D4th)',
                        roundName: 'Consolation Placement (Game #50)'
                    }
                }
            ]
        };
    }

    if (tournamentKey === 'howies-hometown-cup-2026') {
        let pPlayoff = 75;
        let pConsolation = 25;
        if (isPreliminariesComplete && currentRank) {
            pPlayoff = currentRank <= 8 ? 100 : 0;
            pConsolation = currentRank > 8 ? 100 : 0;
        } else if (gamesPlayed === 0) {
            pPlayoff = 75;
            pConsolation = 25;
        } else if (gamesPlayed === 1) {
            pPlayoff = currentPoints >= 3 ? 85 : 40;
            pConsolation = 100 - pPlayoff;
        } else if (gamesPlayed === 2) {
            pPlayoff = currentPoints >= 6 ? 95 : (currentPoints >= 3 ? 65 : 10);
            pConsolation = 100 - pPlayoff;
        }

        return {
            tournamentName: '2026 Howies Hometown Cup',
            overviewSummary: 'All teams are guaranteed 4 games. Following 3 pool games, Saturday Game 4 takes place between 5:00 PM and 8:35 PM EDT. Top 8 seeds advance to the Championship Quarterfinals on Saturday evening, playing for Sunday Semifinal spots.',
            saturdaySpan: '05:00 PM – 08:35 PM EDT',
            timeSpans: [
                {
                    stage: 'Saturday Playoff Quarterfinals (Top 8)',
                    day: 'Saturday, Sep 12',
                    timeSpan: '06:25 PM – 08:30 PM EDT',
                    spanDuration: '2-hour window',
                    locations: ["Griff's Icehouse (East)", 'Eagles Ice Center', 'Kentwood Ice Arena', 'Walker Ice & Fitness'],
                    description: 'Top 8 seeds play Quarterfinal games across 4 Grand Rapids rinks.'
                },
                {
                    stage: 'Saturday Consolation Games (Seeds 9–18)',
                    day: 'Saturday, Sep 12',
                    timeSpan: '05:00 PM – 08:35 PM EDT',
                    spanDuration: '3.5-hour window',
                    locations: ["Griff's Icehouse (East/West)", 'Eagles Ice Center', 'Kentwood Ice Arena'],
                    description: 'Guaranteed 4th game for seeds 9 through 18.'
                },
                {
                    stage: 'Sunday Semifinals',
                    day: 'Sunday, Sep 13',
                    timeSpan: '07:45 AM & 09:10 AM EDT',
                    spanDuration: 'Morning window',
                    locations: ['Eagles Ice Center'],
                    description: 'Semifinals for Saturday Quarterfinal winners.'
                },
                {
                    stage: 'Sunday Championship Final',
                    day: 'Sunday, Sep 13',
                    timeSpan: '02:25 PM EDT',
                    spanDuration: 'Single game',
                    locations: ['Eagles Ice Center'],
                    description: 'Championship game.'
                }
            ],
            scenarios: [
                {
                    finishLabel: 'Top 8 Playoff Qualifier (#1 – #8 Seed)',
                    stageType: 'playoff',
                    stageBadge: 'Championship Quarterfinal',
                    chancePercent: pPlayoff,
                    statusType: getStatus(pPlayoff),
                    game4: {
                        dateTime: 'Saturday, Sep 12 • 06:25 PM – 08:30 PM EDT',
                        time: '06:25 PM – 08:30 PM EDT',
                        location: "Griff's, Eagles, Kentwood, or Walker",
                        opponent: 'Top 8 Seeding Matchup (1v8, 2v7, 3v6, 4v5)',
                        roundName: 'Quarterfinals'
                    },
                    sundayPath: {
                        semifinal: {
                            dateTime: 'Sunday, Sep 13 • 07:45 AM or 09:10 AM EDT',
                            time: '07:45 AM or 09:10 AM EDT',
                            location: 'Eagles Ice Center',
                            opponent: 'Quarterfinal Winner'
                        },
                        championship: {
                            dateTime: 'Sunday, Sep 13 • 02:25 PM EDT',
                            time: '02:25 PM EDT',
                            location: 'Eagles Ice Center',
                            opponent: 'Semifinal Winner'
                        }
                    }
                },
                {
                    finishLabel: 'Consolation Bracket (#9 – #18 Seed)',
                    stageType: 'consolation',
                    stageBadge: 'Consolation Game 4',
                    chancePercent: pConsolation,
                    statusType: getStatus(pConsolation),
                    game4: {
                        dateTime: 'Saturday, Sep 12 • 05:00 PM – 08:35 PM EDT',
                        time: '05:00 PM – 08:35 PM EDT',
                        location: "Griff's, Eagles, or Kentwood",
                        opponent: 'Paired Consolation Opponent',
                        roundName: 'Consolation Placement'
                    }
                }
            ]
        };
    }

    return undefined;
}
